// MacroNode: <div data-type="macro" data-macro="toc|children|...">
// atomic / non-editable block。DecoratorNode を継承して中身を編集不能に。

import { DecoratorNode, $applyNodeReplacement, $getNodeByKey } from "lexical"

const ALLOWED_MACROS = ["toc", "children", "recent-pages", "labels", "embed"]

// embed マクロで許可する src のホスト接尾辞。サーバー側 (HtmlSanitizer::EMBED_HOST_SUFFIXES)
// と一致させること。保存時にサーバーが再検証するが、挿入/編集時にクライアントでも弾く。
const EMBED_HOST_SUFFIXES = [
  "google.com", "youtube.com", "youtube-nocookie.com", "youtu.be",
  "vimeo.com", "figma.com", "speakerdeck.com", "slideshare.net", "miro.com",
]

// 入力 URL を検証・正規化する。許可ホストかつ https (protocol-relative // は https へ寄せる)
// なら埋め込み用 URL へ変換して返し、それ以外は null を返す。
// サーバー側 HtmlSanitizer.allowed_embed_src / embeddable_src と挙動を一致させること。
export function normalizeEmbedSrc(raw) {
  let s = (raw || "").trim()
  if (!s) return null
  if (s.startsWith("//")) s = "https:" + s
  let u
  try { u = new URL(s) } catch { return null }
  if (u.protocol !== "https:") return null
  const host = u.hostname.toLowerCase()
  const ok = EMBED_HOST_SUFFIXES.some((suf) => host === suf || host.endsWith("." + suf))
  return ok ? embeddableSrc(u, host) : null
}

// 主要サービスの閲覧/共有 URL を埋め込み可能な URL へ変換する。
// 既に埋め込み形式・変換ルールの無いものは原文を維持。
function embeddableSrc(u, host) {
  const path = u.pathname
  const google = host === "google.com" || host.endsWith(".google.com")
  let m
  if (google) {
    if ((m = path.match(/^\/presentation\/d\/([^/]+)(?:\/(\w+))?/))) {
      return m[2] === "embed" ? u.href : `https://docs.google.com/presentation/d/${m[1]}/embed`
    }
    if ((m = path.match(/^\/spreadsheets\/d\/([^/]+)(?:\/(\w+))?/))) {
      return ["preview", "pubhtml", "htmlembed"].includes(m[2]) ? u.href : `https://docs.google.com/spreadsheets/d/${m[1]}/preview`
    }
    if ((m = path.match(/^\/document\/d\/([^/]+)(?:\/(\w+))?/))) {
      return m[2] === "preview" ? u.href : `https://docs.google.com/document/d/${m[1]}/preview`
    }
    if ((m = path.match(/^\/forms\/d\/e\/([^/]+)/))) {
      return u.search.includes("embedded=true") ? u.href : `https://docs.google.com/forms/d/e/${m[1]}/viewform?embedded=true`
    }
  }
  if (host === "youtu.be") {
    const id = (path.replace(/^\//, "").match(/^[\w-]+/) || [])[0]
    if (id) return `https://www.youtube.com/embed/${id}`
  } else if (host === "youtube.com" || host.endsWith(".youtube.com")) {
    if (path === "/watch") {
      const v = u.searchParams.get("v")
      if (v && /^[\w-]+$/.test(v)) return `https://www.youtube.com/embed/${v}`
    } else if ((m = path.match(/^\/shorts\/([\w-]+)/))) {
      return `https://www.youtube.com/embed/${m[1]}`
    }
  }
  if (host === "vimeo.com" && (m = path.match(/^\/(\d+)/))) {
    return `https://player.vimeo.com/video/${m[1]}`
  }
  return u.href
}

const EMBED_HINT = "Google Slides / Sheets / YouTube / Vimeo / Figma などの https URL"

// 編集画面で「何のマクロか」を一目で分かるようにするラベル
const MACRO_LABELS = {
  "toc":          { icon: "📑", title: "目次", desc: "見出しから自動生成" },
  "children":     { icon: "📁", title: "子ページ一覧", desc: "このページの子ページ" },
  "recent-pages": { icon: "🕘", title: "最近のページ", desc: "更新が新しい順" },
  "labels":       { icon: "🏷", title: "ラベル", desc: "ラベル一覧" },
  "embed":        { icon: "🔗", title: "埋め込み", desc: "外部コンテンツ" },
}

// embed マクロの URL を prompt で編集する。許可ホストでなければ alert して中断。
function promptEmbedSrc(editor, key) {
  const current = editor.getEditorState().read(() => {
    const n = $getNodeByKey(key)
    return $isMacroNode(n) ? n.getSrc() : ""
  })
  const input = window.prompt(`埋め込み URL\n(${EMBED_HINT})`, current || "")
  if (input === null) return
  const next = normalizeEmbedSrc(input)
  if (!next) {
    window.alert(`対応していない URL です。\n${EMBED_HINT} を指定してください。`)
    return
  }
  editor.update(() => {
    const n = $getNodeByKey(key)
    if ($isMacroNode(n)) n.setSrc(next)
  })
}

export class MacroNode extends DecoratorNode {
  static getType() { return "macro" }

  static clone(node) { return new MacroNode(node.__macro, node.__src, node.__key) }

  // embed マクロは data-src (埋め込み先 URL) を伴う。他マクロでは undefined。
  constructor(macro = "toc", src = "", key) {
    super(key)
    this.__macro = ALLOWED_MACROS.includes(macro) ? macro : "toc"
    this.__src = src || ""
  }

  getMacro() { return this.getLatest().__macro }
  getSrc() { return this.getLatest().__src }
  setSrc(src) { this.getWritable().__src = src || "" }

  isInline()       { return false }
  isIsolated()     { return true }
  isKeyboardSelectable() { return true }

  // 「何のマクロか」が分かるラベルボックスを createDOM で構築する。
  // (Akebono は React 非使用のため decorate の戻り値はマウントされない。
  //  ImageNode/DrawioNode と同様 createDOM で DOM を組み立てる)
  labelHtml() {
    const info = MACRO_LABELS[this.__macro] || { icon: "📦", title: this.__macro, desc: "マクロ" }
    // embed は埋め込み先がひと目で分かるよう desc を URL に差し替える
    const desc = this.__macro === "embed" && this.__src ? this.__src : info.desc
    let html =
      `<span class="akebono-macro-icon">${info.icon}</span>` +
      `<span class="akebono-macro-name">${info.title}</span>` +
      `<span class="akebono-macro-desc">${desc}</span>`
    // embed は「URL を編集」ボタンを併設 (発見しやすさのため)
    if (this.__macro === "embed") {
      html += `<button type="button" class="akebono-macro-edit" data-macro-edit>URL を編集</button>`
    }
    return html
  }

  createDOM(_config, editor) {
    const dom = document.createElement("div")
    dom.setAttribute("data-type", "macro")
    dom.setAttribute("data-macro", this.__macro)
    if (this.__macro === "embed" && this.__src) dom.setAttribute("data-src", this.__src)
    dom.setAttribute("data-lexical-macro", "")
    dom.contentEditable = "false"
    dom.className = "akebono-macro-label"
    dom.innerHTML = this.labelHtml()

    // embed は「URL を編集」ボタンで編集。innerHTML 再構築でボタンが作り直されても
    // 効き続けるよう、コンテナへのイベント委譲で拾う。
    if (this.__macro === "embed" && editor) {
      const key = this.getKey()
      dom.addEventListener("click", (e) => {
        if (!e.target.closest("[data-macro-edit]")) return
        e.preventDefault()
        e.stopPropagation()
        promptEmbedSrc(editor, key)
      })
    }
    return dom
  }

  updateDOM(prev, dom) {
    if (prev.__macro !== this.__macro || prev.__src !== this.__src) {
      dom.setAttribute("data-macro", this.__macro)
      if (this.__macro === "embed" && this.__src) dom.setAttribute("data-src", this.__src)
      else dom.removeAttribute("data-src")
      dom.innerHTML = this.labelHtml()
    }
    return false
  }

  decorate(_editor, _config) { return null }

  static importDOM() {
    return {
      div: (node) => {
        if (node.getAttribute("data-type") !== "macro") return null
        return {
          conversion: (el) => ({
            node: $createMacroNode(el.getAttribute("data-macro") || "toc",
                                   el.getAttribute("data-src") || "")
          }),
          priority: 1,
        }
      },
    }
  }

  exportDOM() {
    const el = document.createElement("div")
    el.setAttribute("data-type", "macro")
    el.setAttribute("data-macro", this.__macro)
    if (this.__macro === "embed" && this.__src) el.setAttribute("data-src", this.__src)
    return { element: el }
  }

  static importJSON(serialized) {
    return $createMacroNode(serialized.macro, serialized.src || "")
  }

  exportJSON() {
    return {
      ...super.exportJSON(),
      type: "macro",
      macro: this.__macro,
      src: this.__src,
      version: 1,
    }
  }
}

export function $createMacroNode(macro = "toc", src = "") {
  return $applyNodeReplacement(new MacroNode(macro, src))
}

export function $isMacroNode(node) {
  return node instanceof MacroNode
}
